import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Hexagon, ArrowRight, CheckCircle2 } from "lucide-react";

const BG =
  "https://images.unsplash.com/photo-1549791084-5f78368b208b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function ResetPassword() {
  const nav = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== confirm) { setErr("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, newPassword: pw });
      setDone(true);
      toast.success("Password updated — you can now sign in.");
    } catch (e2) {
      setErr(apiErr(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="flex items-center justify-center bg-white px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-10 flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center bg-primary text-primary-foreground">
              <Hexagon size={20} strokeWidth={2.5} />
            </div>
            <div className="leading-none">
              <div className="font-display text-base font-black uppercase tracking-tight text-zinc-950">
                Scalio Nexus
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                Reset password
              </div>
            </div>
          </div>

          {done ? (
            <div data-testid="reset-success" className="space-y-6">
              <CheckCircle2 size={40} className="text-emerald-600" />
              <h1 className="font-display text-3xl font-black uppercase tracking-tighter text-zinc-950">
                Password updated
              </h1>
              <p className="text-sm text-zinc-500">Your password has been changed. Use it to sign in.</p>
              <Button
                onClick={() => nav("/login")} data-testid="reset-goto-login"
                className="h-11 w-full rounded-sm bg-primary text-sm font-semibold uppercase tracking-wide hover:bg-[#002480]"
              >
                Back to sign in
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl font-black uppercase tracking-tighter text-zinc-950">
                Choose a new password
              </h1>
              <p className="mt-2 text-sm text-zinc-500">Enter and confirm your new password below.</p>
              <form onSubmit={submit} className="mt-8 space-y-5">
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">New password</Label>
                  <Input
                    type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                    className="rounded-sm border-zinc-300" data-testid="reset-new-password" required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Confirm password</Label>
                  <Input
                    type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="rounded-sm border-zinc-300" data-testid="reset-confirm-password" required
                  />
                </div>
                {err && (
                  <div className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="reset-error">
                    {err}
                  </div>
                )}
                {!token && (
                  <div className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                    Missing or invalid reset link.
                  </div>
                )}
                <Button
                  type="submit" disabled={loading || !token} data-testid="reset-submit-button"
                  className="h-11 w-full rounded-sm bg-primary text-sm font-semibold uppercase tracking-wide hover:bg-[#002480]"
                >
                  {loading ? "Saving…" : "Set new password"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
      <div className="relative hidden lg:block">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/85 mix-blend-multiply" />
      </div>
    </div>
  );
}
